// @ts-nocheck
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { useTheme } from '../contexts/ThemeContext';
import { useLastUpdate } from '../contexts/LastUpdateContext';
import { TrendingUp, Shield, Activity } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import CoinProfileModal from '../components/CoinProfileModal';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

interface TopCoin {
  symbol: string;
  name: string;
  logo_url?: string;
  score?: number;
  current_price?: number;
  price_change?: number;
  position?: string;
}

interface SummaryCardData {
  title: string;
  icon: React.ReactNode;
  description: string;
  coins: TopCoin[];
  viewAllLink: string;
  colorClass: string;
}

const Overview = () => {
  const { isDark } = useTheme();
  const { lastUpdateTime } = useLastUpdate();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [summaryData, setSummaryData] = useState<SummaryCardData[]>([]);
  const [selectedCoin, setSelectedCoin] = useState<string | null>(null);
  const [failedLogos, setFailedLogos] = useState<Set<string>>(new Set());

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token') || localStorage.getItem('access_token');
    return { Authorization: `Bearer ${token}` };
  };

  useEffect(() => {
    fetchSummaryData();
  }, [lastUpdateTime]);

  const fetchSummaryData = async () => {
    try {
      setLoading(true);

      // Fetch top coins from each endpoint
      const [hedgeBtcRes, hedgeEthRes, bmsbUsdRes, bmsbBtcRes, priceBtcRes, priceEthRes] = await Promise.all([
        axios.get(`${API_BASE}/api/hedge-performers?timeframe=1Y&limit=5`, { headers: getAuthHeaders() }),
        axios.get(`${API_BASE}/api/hedge-performers-eth?timeframe=1Y&limit=5`, { headers: getAuthHeaders() }),
        axios.get(`${API_BASE}/api/bull-market-support-band-usd`, { headers: getAuthHeaders() }),
        axios.get(`${API_BASE}/api/bull-market-support-band-btc`, { headers: getAuthHeaders() }),
        axios.get(`${API_BASE}/api/price-comparison-btc?timeframe=1Y`, { headers: getAuthHeaders() }),
        axios.get(`${API_BASE}/api/price-comparison-eth?timeframe=1Y`, { headers: getAuthHeaders() }),
      ]);

      // Log responses to check structure
      console.log('bmsbBtcRes:', bmsbBtcRes.data);
      console.log('priceBtcRes:', priceBtcRes.data);
      console.log('priceEthRes:', priceEthRes.data);

      const cards: SummaryCardData[] = [
        {
          title: '🛡️ Best Hedge vs BTC Falls',
          icon: <Shield className="w-6 h-6" />,
          description: 'Coins that perform well when Bitcoin declines',
          coins: hedgeBtcRes.data.performers?.slice(0, 5).map((coin: any) => ({
            symbol: coin.symbol,
            name: coin.name,
            logo_url: coin.logo_url,
            score: coin.hedge_score,
            price_change: coin.avg_return_on_btc_down_days,
          })) || [],
          viewAllLink: '/explore',
          colorClass: 'blue'
        },
        {
          title: '🛡️ Best Hedge vs ETH Falls',
          icon: <Shield className="w-6 h-6" />,
          description: 'Coins that perform well when Ethereum declines',
          coins: hedgeEthRes.data.performers?.slice(0, 5).map((coin: any) => ({
            symbol: coin.symbol,
            name: coin.name,
            logo_url: coin.logo_url,
            score: coin.hedge_score,
            price_change: coin.avg_return_on_eth_down_days,
          })) || [],
          viewAllLink: '/explore',
          colorClass: 'purple'
        },
        {
          title: '📈 Bull Market Support (USD)',
          icon: <Activity className="w-6 h-6" />,
          description: 'Coins trading above key USD support levels',
          coins: bmsbUsdRes.data?.filter((c: any) => c.position === 'above').slice(0, 5).map((coin: any) => ({
            symbol: coin.symbol,
            name: coin.name,
            logo_url: coin.logo_url,
            position: coin.position,
            current_price: coin.current_price_usd,
          })) || [],
          viewAllLink: '/explore',
          colorClass: 'orange'
        },
        {
          title: '📈 Bull Market Support (BTC)',
          icon: <Activity className="w-6 h-6" />,
          description: 'Coins trading above BTC ratio support levels',
          coins: (Array.isArray(bmsbBtcRes.data) ? bmsbBtcRes.data : [])
            .filter((c: any) => c.position === 'above')
            .slice(0, 5)
            .map((coin: any) => ({
              symbol: coin.symbol,
              name: coin.name,
              logo_url: coin.logo_url,
              position: coin.position,
              current_price: coin.current_price_btc,
            })),
          viewAllLink: '/explore',
          colorClass: 'cyan'
        },
        {
          title: '📊 Outperforming BTC',
          icon: <TrendingUp className="w-6 h-6" />,
          description: 'Coins with stronger returns than Bitcoin',
          coins: (priceBtcRes.data?.comparisons || [])
            .filter((c: any) => c.outperformance > 0)
            .slice(0, 5)
            .map((coin: any) => ({
              symbol: coin.symbol,
              name: coin.name,
              logo_url: coin.logo_url,
              price_change: coin.outperformance,
              current_price: coin.current_price,
            })),
          viewAllLink: '/explore',
          colorClass: 'yellow'
        },
        {
          title: '📊 Outperforming ETH',
          icon: <TrendingUp className="w-6 h-6" />,
          description: 'Coins with stronger returns than Ethereum',
          coins: (priceEthRes.data?.comparisons || [])
            .filter((c: any) => c.outperformance > 0)
            .slice(0, 5)
            .map((coin: any) => ({
              symbol: coin.symbol,
              name: coin.name,
              logo_url: coin.logo_url,
              price_change: coin.outperformance,
              current_price: coin.current_price,
            })),
          viewAllLink: '/explore',
          colorClass: 'indigo'
        }
      ];

      setSummaryData(cards);
    } catch (error) {
      console.error('Error fetching summary data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogoError = (symbol: string) => {
    console.log(`Logo failed to load for: ${symbol}`);
    setFailedLogos(prev => new Set(prev).add(symbol));
  };

  const getCoinLogoUrl = (coin: TopCoin) => {
    if (coin.logo_url) {
      console.log(`Logo URL for ${coin.symbol}:`, coin.logo_url);
      return coin.logo_url;
    }

    // Fallback to hardcoded logo mappings
    const specialMappings: { [key: string]: string } = {
      BTC: 'https://assets.coingecko.com/coins/images/1/small/bitcoin.png',
      ETH: 'https://assets.coingecko.com/coins/images/279/small/ethereum.png',
      BNB: 'https://assets.coingecko.com/coins/images/825/small/bnb-icon2_2x.png',
      XRP: 'https://assets.coingecko.com/coins/images/44/small/xrp-symbol-white-128.png',
      SOL: 'https://assets.coingecko.com/coins/images/4128/small/solana.png',
      ADA: 'https://assets.coingecko.com/coins/images/975/small/cardano.png',
      DOGE: 'https://assets.coingecko.com/coins/images/5/small/dogecoin.png',
      DOT: 'https://assets.coingecko.com/coins/images/12171/small/polkadot.png',
      LINK: 'https://assets.coingecko.com/coins/images/877/small/chainlink-new-logo.png',
      UNI: 'https://assets.coingecko.com/coins/images/12504/small/uni.jpg',
      SHIB: 'https://assets.coingecko.com/coins/images/11939/small/shiba.png',
      TON: 'https://assets.coingecko.com/coins/images/17980/small/ton_symbol.png',
      PEPE: 'https://assets.coingecko.com/coins/images/29850/small/pepe-token.jpeg',
      FLOKI: 'https://assets.coingecko.com/coins/images/16746/small/PNG_image.png',
      AVAX: 'https://assets.coingecko.com/coins/images/12559/small/Avalanche_Circle_RedWhite_Trans.png',
      MATIC: 'https://assets.coingecko.com/coins/images/4713/small/matic-token-icon.png',
      TRX: 'https://assets.coingecko.com/coins/images/1094/small/tron-logo.png',
      ATOM: 'https://assets.coingecko.com/coins/images/1481/small/cosmos_hub.png',
      LTC: 'https://assets.coingecko.com/coins/images/2/small/litecoin.png',
      BCH: 'https://assets.coingecko.com/coins/images/780/small/bitcoin-cash-circle.png',
      NEAR: 'https://assets.coingecko.com/coins/images/10365/small/near.jpg',
      XLM: 'https://assets.coingecko.com/coins/images/100/small/Stellar_symbol_black_RGB.png',
      FIL: 'https://assets.coingecko.com/coins/images/12817/small/filecoin.png',
      HBAR: 'https://assets.coingecko.com/coins/images/3688/small/hbar.png',
      VET: 'https://assets.coingecko.com/coins/images/1167/small/VeChain-Logo-768x725.png',
      APT: 'https://assets.coingecko.com/coins/images/26455/small/aptos_round.png',
      ARB: 'https://assets.coingecko.com/coins/images/16547/small/photo_2023-03-29_21.47.00.jpeg',
      OP: 'https://assets.coingecko.com/coins/images/25244/small/Optimism.png',
      ALGO: 'https://assets.coingecko.com/coins/images/4380/small/download.png',
      ICP: 'https://assets.coingecko.com/coins/images/14495/small/Internet_Computer_logo.png',
      WBTC: 'https://assets.coingecko.com/coins/images/7598/small/wrapped_bitcoin_wbtc.png',
      WETH: 'https://assets.coingecko.com/coins/images/2518/small/weth.png',
      LEO: 'https://assets.coingecko.com/coins/images/8418/small/leo-token.png',
      OKB: 'https://assets.coingecko.com/coins/images/4463/small/WeChat_Image_20220118095654.png',
      NEXO: 'https://assets.coingecko.com/coins/images/3695/small/CG-nexo-token-200x200_2x.png',
      XMR: 'https://assets.coingecko.com/coins/images/69/small/monero_logo.png',
      ZEC: 'https://assets.coingecko.com/coins/images/486/small/circle-zcash-color.png',
    };

    const symbol = coin.symbol.toUpperCase();
    if (specialMappings[symbol]) {
      console.log(`Using fallback logo for ${coin.symbol}`);
      return specialMappings[symbol];
    }

    // Final fallback to CoinCap
    console.log(`No logo_url for ${coin.symbol}, using CoinCap fallback`);
    return `https://assets.coincap.io/assets/icons/${coin.symbol.toLowerCase()}@2x.png`;
  };

  const getColorClasses = (colorClass: string) => {
    const colors = {
      emerald: {
        bg: isDark ? 'bg-emerald-500/10' : 'bg-emerald-50',
        border: isDark ? 'border-emerald-500/30' : 'border-emerald-200',
        text: isDark ? 'text-emerald-400' : 'text-emerald-600',
        hover: isDark ? 'hover:bg-emerald-500/20' : 'hover:bg-emerald-100'
      },
      blue: {
        bg: isDark ? 'bg-blue-500/10' : 'bg-blue-50',
        border: isDark ? 'border-blue-500/30' : 'border-blue-200',
        text: isDark ? 'text-blue-400' : 'text-blue-600',
        hover: isDark ? 'hover:bg-blue-500/20' : 'hover:bg-blue-100'
      },
      purple: {
        bg: isDark ? 'bg-purple-500/10' : 'bg-purple-50',
        border: isDark ? 'border-purple-500/30' : 'border-purple-200',
        text: isDark ? 'text-purple-400' : 'text-purple-600',
        hover: isDark ? 'hover:bg-purple-500/20' : 'hover:bg-purple-100'
      },
      orange: {
        bg: isDark ? 'bg-orange-500/10' : 'bg-orange-50',
        border: isDark ? 'border-orange-500/30' : 'border-orange-200',
        text: isDark ? 'text-orange-400' : 'text-orange-600',
        hover: isDark ? 'hover:bg-orange-500/20' : 'hover:bg-orange-100'
      },
      cyan: {
        bg: isDark ? 'bg-cyan-500/10' : 'bg-cyan-50',
        border: isDark ? 'border-cyan-500/30' : 'border-cyan-200',
        text: isDark ? 'text-cyan-400' : 'text-cyan-600',
        hover: isDark ? 'hover:bg-cyan-500/20' : 'hover:bg-cyan-100'
      },
      yellow: {
        bg: isDark ? 'bg-yellow-500/10' : 'bg-yellow-50',
        border: isDark ? 'border-yellow-500/30' : 'border-yellow-200',
        text: isDark ? 'text-yellow-400' : 'text-yellow-600',
        hover: isDark ? 'hover:bg-yellow-500/20' : 'hover:bg-yellow-100'
      },
      indigo: {
        bg: isDark ? 'bg-indigo-500/10' : 'bg-indigo-50',
        border: isDark ? 'border-indigo-500/30' : 'border-indigo-200',
        text: isDark ? 'text-indigo-400' : 'text-indigo-600',
        hover: isDark ? 'hover:bg-indigo-500/20' : 'hover:bg-indigo-100'
      },
      pink: {
        bg: isDark ? 'bg-pink-500/10' : 'bg-pink-50',
        border: isDark ? 'border-pink-500/30' : 'border-pink-200',
        text: isDark ? 'text-pink-400' : 'text-pink-600',
        hover: isDark ? 'hover:bg-pink-500/20' : 'hover:bg-pink-100'
      }
    };
    return colors[colorClass] || colors.emerald;
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
          <div className="px-4 py-6 sm:px-0">
            <div className="flex items-center justify-center min-h-[60vh]">
              <div className="text-center">
                <div className={`inline-block animate-spin rounded-full h-12 w-12 border-b-2 ${isDark ? 'border-emerald-400' : 'border-orange-600'}`}></div>
                <p className={`mt-4 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Loading market overview...</p>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {selectedCoin && (
        <CoinProfileModal
          symbol={selectedCoin}
          onClose={() => setSelectedCoin(null)}
        />
      )}
      <div className={`min-h-screen ${isDark ? 'bg-zinc-900' : 'bg-gray-50'}`}>
        {/* Hero Section */}
        <div className="relative overflow-hidden">
          {/* Background gradient */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600 opacity-10"></div>
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnoiIHN0cm9rZT0iIzAwMCIgb3BhY2l0eT0iLjA1Ii8+PC9nPjwvc3ZnPg==')] opacity-30"></div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
            <div className="text-center">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-3 sm:mb-4">
                <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                  Market
                </span>
                <br />
                <span className={isDark ? 'text-white' : 'text-gray-900'}>Overview</span>
              </h1>
              <p className={`text-base sm:text-lg mb-3 sm:mb-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Quick insights across all trading strategies
              </p>
              <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20">
                <Activity className="w-3 sm:w-4 h-3 sm:h-4 text-blue-500" />
                <span className={`text-xs sm:text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Live Market Data
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
          {/* AI Strategy Advisor Card - Prominent placement at top */}
          <div className={`mb-6 sm:mb-8 rounded-2xl shadow-lg border-2 transition-all duration-200 ${
            isDark
              ? 'bg-gradient-to-br from-purple-900/20 to-blue-900/20 border-purple-500/30 hover:border-purple-500/50'
              : 'bg-gradient-to-br from-purple-50 to-blue-50 border-purple-200 hover:border-purple-300'
          }`}>
            <div className="px-6 py-5">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-start space-x-4">
                  <div className={`p-3 rounded-xl ${
                    isDark ? 'bg-purple-500/20' : 'bg-purple-100'
                  }`}>
                    <span className="text-4xl">🤖</span>
                  </div>
                  <div>
                    <h2 className={`text-xl sm:text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      AI Strategy Advisor
                    </h2>
                    <p className={`text-sm sm:text-base mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      Get AI-powered recommendations by analyzing all 6 trading strategies together
                    </p>
                    <div className="flex flex-wrap items-center gap-2 mt-2">
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        isDark ? 'bg-purple-500/20 text-purple-300' : 'bg-purple-100 text-purple-700'
                      }`}>
                        Cross-Strategy Analysis
                      </span>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        isDark ? 'bg-blue-500/20 text-blue-300' : 'bg-blue-100 text-blue-700'
                      }`}>
                        Risk Assessment
                      </span>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        isDark ? 'bg-green-500/20 text-green-300' : 'bg-green-100 text-green-700'
                      }`}>
                        Actionable Insights
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => navigate('/strategy-advisor')}
                  className={`px-6 py-3 rounded-lg font-semibold shadow-md transition-all duration-200 whitespace-nowrap ${
                    isDark
                      ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:from-purple-500 hover:to-blue-500 hover:shadow-lg'
                      : 'bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:from-purple-700 hover:to-blue-700 hover:shadow-lg'
                  }`}
                >
                  Get AI Recommendations →
                </button>
              </div>
            </div>
          </div>

          {/* Summary Cards Grid - 3 columns on desktop, 2 on tablet, 1 on mobile */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {summaryData.map((card, idx) => {
              const colors = getColorClasses(card.colorClass);
              return (
                <div
                  key={idx}
                  className={`rounded-2xl shadow-sm border transition-all duration-200 ${
                    isDark
                      ? 'bg-white/[0.03] border-white/10 hover:border-white/20'
                      : 'bg-white border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {/* Card Header */}
                  <div className={`px-6 py-4 border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className={`p-2 rounded-lg ${colors.bg}`}>
                          <span className={colors.text}>{card.icon}</span>
                        </div>
                        <div>
                          <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {card.title}
                          </h3>
                          <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                            {card.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Coins List */}
                  <div className="px-6 py-4">
                    {card.coins.length === 0 ? (
                      <div className={`text-center py-8 text-sm ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                        No data available
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {card.coins.map((coin, coinIdx) => (
                          <div
                            key={coinIdx}
                            className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors ${
                              isDark ? 'hover:bg-white/[0.05]' : 'hover:bg-gray-50'
                            }`}
                            onClick={() => setSelectedCoin(coin.symbol)}
                          >
                            <div className="flex items-center space-x-3">
                              {failedLogos.has(coin.symbol) ? (
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-xs font-bold ${colors.bg} ${colors.text}`}>
                                  {coin.symbol.slice(0, 3).toUpperCase()}
                                </div>
                              ) : (
                                <img
                                  src={getCoinLogoUrl(coin)}
                                  alt={coin.symbol}
                                  className="w-10 h-10 rounded-full"
                                  onError={() => handleLogoError(coin.symbol)}
                                />
                              )}
                              <div>
                                <div className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                  {coin.symbol}
                                </div>
                                <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                                  {coin.name || coin.symbol}
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              {coin.score !== undefined && (
                                <div className={`text-sm font-semibold ${colors.text}`}>
                                  {coin.score.toFixed(1)}
                                </div>
                              )}
                              {coin.current_price !== undefined && (
                                <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                  ${coin.current_price.toLocaleString()}
                                </div>
                              )}
                              {coin.price_change !== undefined && (
                                <div className={`text-xs ${
                                  coin.price_change >= 0
                                    ? (isDark ? 'text-emerald-400' : 'text-green-600')
                                    : (isDark ? 'text-red-400' : 'text-red-600')
                                }`}>
                                  {coin.price_change >= 0 ? '+' : ''}{coin.price_change.toFixed(2)}%
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* View All Footer */}
                  <div className={`px-6 py-3 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                    <button
                      onClick={() => navigate(card.viewAllLink)}
                      className={`w-full text-center text-sm font-medium ${colors.text} ${colors.hover} py-2 rounded-lg transition-colors`}
                    >
                      View All →
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Quick Actions */}
          <div className={`mt-6 sm:mt-8 p-4 sm:p-6 rounded-2xl border ${
            isDark ? 'bg-white/[0.03] border-white/10' : 'bg-gray-50 border-gray-200'
          }`}>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <h3 className={`text-base sm:text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Need more details?
                </h3>
                <p className={`text-xs sm:text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Explore full tables or search for specific coins
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
                <button
                  onClick={() => navigate('/explore')}
                  className={`px-4 sm:px-6 py-2 rounded-lg font-medium transition-colors text-center ${
                    isDark
                      ? 'bg-emerald-500 text-white hover:bg-emerald-600'
                      : 'bg-orange-600 text-white hover:bg-orange-700'
                  }`}
                >
                  Explore All Tables
                </button>
                <button
                  onClick={() => navigate('/search')}
                  className={`px-4 sm:px-6 py-2 rounded-lg font-medium border transition-colors text-center ${
                    isDark
                      ? 'border-white/20 text-white hover:bg-white/5'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  Search Coins
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Overview;
